import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { FrenzyApiModes } from '../../../types/frenzy/query';
import { FilterQueryParam } from '../../../types/search/search';

interface UseZeroResultsSentryLoggingParams {
  listingsAreLoaded: boolean;
  totalItems: number;
  areFiltersApplied: boolean;
  searchParams: Record<string, any>;
  treetId: string;
  shopName: string;
}

/**
 * Custom hook to log to Sentry when a filtered search produces zero results from URL parameter changes
 */
export function useZeroResultsFromUrlChangeSentryLogging({
  listingsAreLoaded,
  totalItems,
  areFiltersApplied,
  searchParams,
  treetId,
  shopName,
}: UseZeroResultsSentryLoggingParams): void {
  const location = useLocation();

  // Track the previous search params to detect changes
  // Will be undefined on initial page load, defined on subsequent renders
  const prevSearchRef = useRef<string>();

  useEffect(() => {
    // On page load (app reload), prevSearchRef.current is undefined
    // On internal route changes, prevSearchRef.current contains the previous search string
    const isPageLoad = prevSearchRef.current === undefined;
    const didSearchParamsChange = prevSearchRef.current !== location.search;

    // Update ref for next render
    prevSearchRef.current = location.search;

    // Only run for internal route changes when search params change
    const isUrlChangeFromInternalRouteChange =
      location.state !== undefined && location.state !== null;

    if (!isPageLoad && isUrlChangeFromInternalRouteChange && !didSearchParamsChange) {
      // Internal route change but search params didn't change - skip
      return;
    }

    /**
     * On app load check if the previous referrer was from the Treet App.
     * This happens when we use an anchor tag instead of a React Router component
     * Builder links reload the app this way because it uses anchor tags.
     */
    const isUrlChangeFromTreetAppPageLoad =
      isPageLoad &&
      typeof window !== 'undefined' &&
      document.referrer &&
      new URL(document.referrer).origin === window.location.origin;

    // Early returns for conditions we don't want to log
    if (!listingsAreLoaded || totalItems > 0) return; // Skip searches with results
    if (searchParams.mode !== FrenzyApiModes.FilterChange) return; // Skip non-filter-change modes
    if (!areFiltersApplied) return; // Skip searches without filters applied

    // Skip searches that use keywords
    const { [FilterQueryParam.keywords]: keywords } = searchParams;
    if (keywords) return;

    // Determine if the URL change came from within the Treet app
    const isUrlChangeFromTreetApp =
      isUrlChangeFromTreetAppPageLoad || isUrlChangeFromInternalRouteChange;

    // Log to Sentry
    Sentry.withScope((scope) => {
      scope.setTag('event_type', 'zero_search_results_with_filters_from_treet_app_filter_change');
      scope.setTag('treet_id', treetId);
      scope.setTag('shop_name', shopName);
      scope.setTag('is_filter_change_from_treet_app', isUrlChangeFromTreetApp);

      scope.setExtra('search_params', searchParams);
      scope.setExtra('url', typeof window !== 'undefined' ? window.location.href : 'unknown');
      scope.setExtra(
        'referrer',
        typeof document !== 'undefined' && document.referrer ? document.referrer : 'unknown'
      );

      scope.setFingerprint(['zero-search-results-filter-change', treetId]);

      Sentry.captureMessage(
        `Search with filters returned zero results from URL - Shop: ${shopName}`,
        'warning'
      );
    });
  }, [
    listingsAreLoaded,
    totalItems,
    areFiltersApplied,
    searchParams,
    treetId,
    shopName,
    location.search,
    location.state,
  ]);
}
